import React, {useEffect, useState} from 'react';
import {Button} from '@material-ui/core';
import {
  reduxAddProduct,
  reduxToggleSidePanel,
  reduxUpdateProduct,
  reduxUpdateProductDepotInfo,
} from 'appredux/actions';
import CheckIcon from 'components/CheckIcon/CheckIcon';
import {Route} from 'react-router-dom';
import {
  IFarmInputProductDepotInfoInput,
  IBug,
  IDepot,
  IFarmInputProduct,
  IFarmInputProductInput,
  IFarmInputProductSubCategory,
  IFarmInputProductSubSubCategory,
  IProvince,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {
  apiGetBugs,
  apiGetInputProduct,
  apiGetInputProductSubCategories,
  apiGetInputProductSubSubCategories,
  apiGetProductDepotInfo,
  apiGetProvinces,
  apiProductExistsWithStockCode,
  apiSaveProductDepotInfo,
  apiUpdateInputProduct,
} from 'constants/Api';
import {ProductForm} from 'components/ProductForm/ProductForm';
import {Skeleton} from '@material-ui/lab';
import {PermissionsService} from 'services/PermissionsService';
import {ESupplierUserPermissions} from 'constants/index';

export default function AddOrUpdateProduct(props: {
  user: ISupplierUserProfile;
  allDepots: IDepot[];
  allProducts: IFarmInputProduct[];
  product?: IFarmInputProduct;
  isCopy?: boolean;
}) {
  const [product, setProduct] = useState<IFarmInputProduct>();
  const [isLoadingProductDetail, setIsLoadingProductDetail] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [hasSuccessfullyUpdated, setHasSuccessfullyUpdated] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [subCategories, setSubCategories] = useState<
    IFarmInputProductSubCategory[]
  >([]);
  const [subSubCategories, setSubSubCategories] = useState<
    IFarmInputProductSubSubCategory[]
  >([]);
  const [isLoadingProvinces, setIsLoadingProvinces] = useState(false);
  const [provinces, setProvinces] = useState<IProvince[]>([]);
  const [isLoadingBugs, setIsLoadingBugs] = useState(false);
  const [bugs, setBugs] = useState<IBug[]>([]);

  const isAdding = !props.product;
  // const isUpdating = !isAdding;

  useEffect(() => {
    if (!props.product) return;
    setIsLoadingProductDetail(true);
    apiGetInputProduct(props.product.id.toString())
      .then(setProduct)
      .catch((err) => {
        alert('Failed to load product at the moment. Please try again later');
        console.error('Failed to load product detail', err);
      })
      .finally(() => setIsLoadingProductDetail(false));
  }, [props.product?.id]);

  // todo should fetch product categories globally
  useEffect(() => {
    setIsLoadingCategories(true);
    apiGetInputProductSubCategories()
      .then(setSubCategories)
      .then(() => apiGetInputProductSubSubCategories())
      .then((subSubCategories) => subSubCategories.sort(sortSubSubCategories))
      .then(setSubSubCategories)
      .catch((err) => {
        alert(
          'Failed to load categories at the moment. Please try again later',
        );
        console.error('Failed to load categories', err);
      })
      .finally(() => setIsLoadingCategories(false));
  }, []);

  // todo should fetch provinces globally
  useEffect(() => {
    setIsLoadingProvinces(true);
    apiGetProvinces()
      .then(setProvinces)
      .catch((err) => {
        alert('Failed to load provinces at the moment. Please try again later');
        console.error('Failed to load provinces', err);
      })
      .finally(() => setIsLoadingProvinces(false));
  }, []);

  // todo should fetch bugs globally
  useEffect(() => {
    setIsLoadingBugs(true);
    apiGetBugs()
      .then(setBugs)
      .catch((err) => {
        alert('Failed to load bugs at the moment. Please try again later');
        console.error('Failed to load bugs', err);
      })
      .finally(() => setIsLoadingBugs(false));
  }, []);

  const sortSubSubCategories = (
    a: IFarmInputProductSubSubCategory,
    b: IFarmInputProductSubSubCategory,
  ) => {
    // sort by subCategoryId then name
    const sort1 = a.subCategoryId.localeCompare(b.subCategoryId);
    if (sort1 != 0) return sort1;
    return a.name.localeCompare(b.name);
  };

  const onSubmitProduct = (submission: IFarmInputProductInput) => {
    if (!canWriteProduct) return;
    setIsLoading(true);
    const product: IFarmInputProductInput = {
      ...(submission.id && !props.isCopy && {id: submission.id}),
      name: submission.name,
      costPrice: submission.costPrice,
      price: submission.price,
      category: submission.category,
      stockCode: submission.stockCode,
      size: submission.size,
      measurement: submission.measurement,
      grade: submission.grade || '',
      image: submission.image,
      activeIngredient: submission.activeIngredient,
      tradeName: submission.tradeName,
      labelLink: submission.labelLink || '',
      subCategoryId: submission.subCategoryId,
      subCategoryName: submission.subCategoryName,
      isActive: submission.isActive,
      mainInputSupplierId: submission.mainInputSupplierId,
      brandSupplierId: submission.brandSupplierId,
      provincialRestrictions: submission.provincialRestrictions,
      lengthCm: submission.lengthCm,
      widthCm: submission.widthCm,
      heightCm: submission.heightCm,
      weightKg: submission.weightKg,
      description: submission.description,
      relatedProducts: submission.relatedProducts,
      bugs: submission.bugs,
    };
    Promise.resolve()
      .then(async () => {
        if (!isAdding && !props.isCopy) return;
        const productExists = await apiProductExistsWithStockCode(
          product.stockCode,
        );
        if (productExists) {
          alert('Product with stock code already exists on system');
          throw new Error('Product with stock code already exists on system');
        }
      })
      .then(() => apiUpdateInputProduct(product))
      .then((productUpdate) => {
        isAdding || props.isCopy
          ? reduxAddProduct(productUpdate)
          : reduxUpdateProduct(productUpdate);
        setHasSuccessfullyUpdated(true);
        return productUpdate;
      })
      .then(async (productUpdate) => {
        const singlePriceList = !props.user.company?.depotPriceList;
        if (singlePriceList) {
          const inventoryUpdates: IFarmInputProductDepotInfoInput[] =
            props.allDepots.map((depot) => ({
              depotId: depot.id.toString(),
              productId: productUpdate.id.toString(),
              isActive: true,
              price: productUpdate.price,
              costPrice: productUpdate.costPrice,
            }));
          await apiSaveProductDepotInfo(inventoryUpdates);

          const productIds = [productUpdate.id.toString()];
          const depotIds = props.allDepots.map((depot) => depot.id.toString());
          const updates = await apiGetProductDepotInfo({productIds, depotIds});
          reduxUpdateProductDepotInfo(updates);
        }
      })
      .catch((err) => {
        alert('Failed to update product at the moment. Please try again later');
        console.error('Failed to update product', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const permissions = new PermissionsService(props.user);
  const canWriteProduct =
    !permissions.shouldShowProductsGlobalViewOnly() &&
    permissions.hasPermission(ESupplierUserPermissions.PRODUCT_WRITE);

  if (hasSuccessfullyUpdated) {
    return (
      <Route
        render={() => (
          <div className="side-panel-success-container">
            <div className="mid-section">
              <div className="success-gif">
                <CheckIcon />
              </div>
              <div className="success-msg">
                {isAdding || props.isCopy
                  ? 'Product added successfully'
                  : 'Product updated successfully'}
              </div>
            </div>
            <div className="btn-container">
              <Button
                className="primary-btn"
                type="submit"
                variant="outlined"
                color="primary"
                disabled={isLoading}
                onClick={() => reduxToggleSidePanel(false, null)}
              >
                View Products
              </Button>
            </div>
          </div>
        )}
      />
    );
  }

  const LoadingForm = () => (
    <div className="form">
      <Skeleton variant={'rect'} height={600} />
    </div>
  );

  if (isLoadingProductDetail) return <LoadingForm />;

  return (
    <ProductForm
      user={props.user}
      product={product}
      subCategories={subCategories}
      subSubCategories={subSubCategories}
      isLoading={isLoading}
      isLoadingCategories={isLoadingCategories}
      isLoadingProvinces={isLoadingProvinces}
      provinces={provinces}
      isLoadingBugs={isLoadingBugs}
      bugs={bugs}
      allProducts={props.allProducts}
      submitButtonTitle={isAdding || props.isCopy ? 'Add' : 'Update'}
      onSubmit={onSubmitProduct}
      cancelButtonTitle={'Cancel'}
      onCancel={() => reduxToggleSidePanel(false)}
      readonly={!canWriteProduct}
    />
  );
}
